import { Link, useNavigate } from "react-router-dom";
import { MapPin, Maximize, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { goToEnquiry } from "@/lib/scroll";

const STATUS_STYLES = {
  Available: "bg-gold text-navy",
  Reserved: "bg-navy text-white",
  Sold: "bg-gray-500 text-white",
};

export default function PropertyCard({ property: p, index = 0 }) {
  const navigate = useNavigate();

  return (
    <Reveal delay={Math.min(index * 0.07, 0.3)} className="h-full">
      <article
        data-testid="property-card"
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-transform duration-300 hover:-translate-y-1"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={p.images?.[0]}
            alt={`${p.title} - ${p.property_type} plots in ${p.location}, Nagpur`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
          <span
            data-testid="property-status-badge"
            className={`absolute left-4 top-4 rounded-sm px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLES[p.status] || STATUS_STYLES.Available}`}
          >
            {p.status}
          </span>
          {p.is_sample && (
            <span className="absolute right-4 top-4 rounded-sm bg-white/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-navy/70 backdrop-blur">
              Sample Listing
            </span>
          )}
          <span className="absolute bottom-4 left-4 rounded-sm bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-navy backdrop-blur">
            {p.property_type}
          </span>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-display text-xl font-semibold text-navy">{p.title}</h3>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-gold-dark" aria-hidden="true" />
            {p.location}
          </p>
          <p className="mt-1.5 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Maximize className="h-3.5 w-3.5 shrink-0 text-gold-dark" aria-hidden="true" />
            {p.plot_size}
          </p>
          {p.location_advantage && (
            <p className="mt-3 border-l-2 border-gold/50 pl-3 text-xs leading-relaxed text-muted-foreground">
              {p.location_advantage}
            </p>
          )}
          <p className="mt-4 font-display text-lg font-bold text-navy" data-testid="property-price">
            {p.price_label}
          </p>

          <div className="mt-5 flex gap-3 pt-1">
            <Link
              to={`/properties/${p.id}`}
              data-testid="view-details-button"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-sm border border-navy/20 px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:border-navy hover:bg-navy hover:text-white"
            >
              View Details
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <button
              data-testid="enquire-now-button"
              onClick={() => goToEnquiry(navigate, p.title)}
              className="flex-1 rounded-sm bg-gold px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-gold-light"
            >
              Enquire Now
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
}
